import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, ClipSpinner } from '~/components/ui';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import { Modal } from '~/components/overlays/Modal';
import { formatLengthWithBasis } from '~/utils';
import type {
  GetPackingListQuery,
  OrderStatus,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';
import { PackedItemsList } from '~/pages/order-view/sections/PackedItemsList';
import { PackingListTotals } from '~/pages/order-view/sections/PackingListTotals';
import { PackingListNotes } from '~/pages/order-view/sections/PackingListNotes';
import { computePackingListDiff } from '~/pages/order-view/sections/packingListDiff';
import { AddPackingListItemModal } from '~/pages/order-view/sections/AddPackingListItemModal';
import type { PlatformProductsLength } from '~/utils/order';

function toProductLabel(
  name: string,
  lengthDisplayValue: string,
  isNominal: boolean | null | undefined
): string {
  return `${name} – ${formatLengthWithBasis(lengthDisplayValue, isNominal)}`;
}

type PlatformProductsLengthDimensions = {
  isNominal?: boolean | null;
  platformProduct: {
    nominalWidthInches?: number | null;
    actualWidthInches?: number | null;
    nominalThicknessInches?: number | null;
    actualThicknessInches?: number | null;
    actualLengthInches?: number | null;
    piecesPerBundle: { value: number; unit: UnitCode };
  };
  length: {
    nominalLengthInches?: number | null;
    actualLengthInches: number;
  };
};

type RowState = {
  platformProductLengthId: string;
  productLabel: string;
  quantityDisplay: number;
  unitLabel: UnitCode;
  platformProductsLength: PlatformProductsLengthDimensions;
  /** Backend only falls back to this when no order-item/seller-config price exists; ignored otherwise. */
  price?: number;
  piecesPerBundleOverride?: number | null;
};

export type SellerProduct = {
  platformProductLength: PlatformProductsLengthDimensions & {
    id: string;
    lengthDisplayValue: string;
    platformProduct: { name: string; standardUnitsCode: UnitCode };
  };
  pricePerUnit: { amount: number; currency: string } | null | undefined;
};

const VISIBLE_STATUSES: OrderStatus[] = ['CONFIRMED', 'SHIPPED', 'DELIVERED'];

export type PackingList = NonNullable<GetPackingListQuery['packingList']>;

export type OrderItem = {
  platformProductsLengths: PlatformProductsLengthDimensions & {
    id: string;
    lengthDisplayValue: string;
    platformProduct: { name: string };
  };
  price?: { amount: number; currency: string } | null;
  piecesPerBundleOverride?: number | null;
  effectivePiecesPerBundle: number;
  quantity: { value: number; unit: UnitCode };
};

type PackingListViewProps = {
  orderId: string;
  orderItems: OrderItem[];
  orderStatus: OrderStatus;
  packingList: PackingList | null;
  isLoadingPL: boolean;
  products: SellerProduct[];
  isSaving: boolean;
  useSellerAddFlow?: boolean;
  onSavePackingList: (args: {
    orderId: string;
    items: Array<{
      platformProductLengthId: string;
      quantity: { value: number; unit: UnitCode };
      price?: number;
      piecesPerBundleOverride?: number | null;
    }>;
    notes?: string;
  }) => Promise<unknown>;
  onPublishPackingList: (args: { orderId: string }) => Promise<unknown>;
  currency?: string;
};

export function PackingListView({
  orderId,
  orderItems,
  orderStatus,
  packingList,
  isLoadingPL,
  products,
  isSaving,
  useSellerAddFlow = true,
  onSavePackingList,
  onPublishPackingList,
  currency = 'USD',
}: PackingListViewProps) {
  const { t } = useTranslation('order');
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState<RowState[]>([]);
  const [notes, setNotes] = useState('');
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  const orderItemByPplId = new Map(
    orderItems.map((item) => [item.platformProductsLengths.id, item])
  );
  const orderLinePiecesPerBundleFor = (pplId: string): number | undefined => {
    const size = orderItemByPplId.get(pplId)?.effectivePiecesPerBundle;
    return size && size > 0 ? size : undefined;
  };

  const handleStartEditing = () => {
    if (packingList) {
      // EDIT mode: pre-fill from existing packing list items
      setRows(
        packingList.items.map((item) => ({
          platformProductLengthId: item.platformProductsLengths.id,
          productLabel: toProductLabel(
            item.platformProductsLengths.platformProduct.name,
            item.platformProductsLengths.lengthDisplayValue,
            item.platformProductsLengths.isNominal
          ),
          quantityDisplay: item.quantity.value,
          unitLabel: item.quantity.unit,
          platformProductsLength: {
            isNominal: item.platformProductsLengths.isNominal,
            platformProduct: item.platformProductsLengths.platformProduct,
            length: item.platformProductsLengths.length,
          },
          price: item.price?.amount,
          piecesPerBundleOverride: item.piecesPerBundleOverride,
        }))
      );
      setNotes(packingList.notes ?? '');
    } else {
      // CREATE mode: pre-fill from order items
      setRows(
        orderItems.map((item) => ({
          platformProductLengthId: item.platformProductsLengths.id,
          productLabel: toProductLabel(
            item.platformProductsLengths.platformProduct.name,
            item.platformProductsLengths.lengthDisplayValue,
            item.platformProductsLengths.isNominal
          ),
          quantityDisplay: item.quantity.value,
          unitLabel: item.quantity.unit,
          platformProductsLength: {
            isNominal: item.platformProductsLengths.isNominal,
            platformProduct: item.platformProductsLengths.platformProduct,
            length: item.platformProductsLengths.length,
          },
          piecesPerBundleOverride: item.piecesPerBundleOverride,
        }))
      );
      setNotes('');
    }
    setIsEditing(true);
  };

  // Early return guard: component is not visible for DRAFT and PENDING_SELLER_CONFIRMATION statuses
  if (!VISIBLE_STATUSES.includes(orderStatus)) return null;

  const isReadOnly = orderStatus !== 'CONFIRMED';

  const handleSave = async () => {
    if (rows.length === 0) return;
    try {
      await onSavePackingList({
        orderId,
        items: rows.map((row) => ({
          platformProductLengthId: row.platformProductLengthId,
          quantity: { value: row.quantityDisplay, unit: row.unitLabel },
          ...(row.price !== undefined ? { price: row.price } : {}),
          piecesPerBundleOverride: row.piecesPerBundleOverride ?? null,
        })),
        notes,
      });
      try {
        await onPublishPackingList({ orderId });
        setIsEditing(false);
        toast.success(t('order.packing_list.save_success'));
      } catch {
        toast.error(t('order.packing_list.save_error'));
      }
    } catch {
      toast.error(t('order.packing_list.save_error'));
    }
  };

  const handleAddRow = (pplId: string) => {
    const spc = products.find((p) => p?.platformProductLength.id === pplId);
    if (!spc) return;
    setRows((prev) => [
      ...prev,
      {
        platformProductLengthId: spc.platformProductLength.id,
        productLabel: toProductLabel(
          spc.platformProductLength.platformProduct.name,
          spc.platformProductLength.lengthDisplayValue,
          spc.platformProductLength.isNominal
        ),
        quantityDisplay: 1,
        unitLabel: spc.platformProductLength.platformProduct.standardUnitsCode,
        platformProductsLength: {
          isNominal: spc.platformProductLength.isNominal,
          platformProduct: spc.platformProductLength.platformProduct,
          length: spc.platformProductLength.length,
        },
      },
    ]);
  };

  const handleAddBuyerItem = ({
    platformProductLength,
    quantity,
    price,
    piecesPerBundleOverride,
  }: {
    platformProductLength: PlatformProductsLength;
    quantity: { value: number; unit: UnitCode };
    price: number;
    piecesPerBundleOverride?: number;
  }) => {
    setRows((prev) => [
      ...prev,
      {
        platformProductLengthId: platformProductLength.id,
        productLabel: toProductLabel(
          platformProductLength.platformProduct.name,
          platformProductLength.lengthDisplayValue,
          platformProductLength.isNominal
        ),
        quantityDisplay: quantity.value,
        unitLabel: quantity.unit,
        platformProductsLength: {
          isNominal: platformProductLength.isNominal,
          platformProduct: platformProductLength.platformProduct,
          length: platformProductLength.length,
        },
        price,
        piecesPerBundleOverride,
      },
    ]);
    setShowAddItemModal(false);
  };

  const setPiecesPerBundleOverride = (
    idx: number,
    value: number | null,
    quantity: Quantity
  ) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              piecesPerBundleOverride: value,
              quantityDisplay: quantity.value,
              unitLabel: quantity.unit,
            }
          : r
      )
    );
  };

  if (isLoadingPL) return <ClipSpinner />;

  // No packing list exists for a read-only order (SHIPPED/DELIVERED)
  if (isReadOnly && !packingList) return null;

  // VIEW state A (read-only) and VIEW state B (editable, not editing): shared card layout
  if (
    (isReadOnly && packingList != null) ||
    (!isReadOnly && packingList != null && !isEditing)
  ) {
    const originalTotal = orderItems.reduce(
      (s, item) => s + (item.price?.amount ?? 0) * item.quantity.value,
      0
    );
    const packedTotal = packingList.items.reduce(
      (s, item) => s + item.price.amount * item.quantity.value,
      0
    );
    const variance = packedTotal - originalTotal;

    const diffRows = computePackingListDiff(orderItems, packingList.items);
    const diffByPplId = new Map(diffRows.map((r) => [r.pplId, r]));

    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mt-6">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-neut-prim font-semibold">
            {t('order.packing_list.heading')}
          </h2>
          {!isReadOnly && (
            <Button onClick={handleStartEditing}>
              {t('order.packing_list.edit_button')}
            </Button>
          )}
        </div>

        {/* Per-item cards */}
        <PackedItemsList
          packingList={packingList}
          orderItemByPplId={orderItemByPplId}
          diffByPplId={diffByPplId}
        />

        {/* Footer summary row */}
        <PackingListTotals
          originalTotal={originalTotal}
          packedTotal={packedTotal}
          variance={variance}
          currency={currency}
        />

        {/* Packing Notes */}
        <PackingListNotes notes={packingList.notes} />
      </div>
    );
  }

  // CREATE state (CTA): CONFIRMED with no packing list, not currently editing
  if (!isReadOnly && packingList == null && !isEditing) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mt-6">
        <div className="p-6">
          <h2 className="text-neut-prim font-semibold mb-2">
            {t('order.packing_list.create_heading')}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t('order.packing_list.create_description')}
          </p>
          <Button onClick={handleStartEditing}>
            {t('order.packing_list.create_button')}
          </Button>
        </div>
      </div>
    );
  }

  // EDITING state (shared for both CREATE and EDIT): full editable form
  const usedPplIds = new Set(rows.map((r) => r.platformProductLengthId));
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm mt-6">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-neut-prim font-semibold">
          {t('order.packing_list.heading')}
        </h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Top-level Packing Notes (no maxLength — TEXT column is unlimited per D-P3-12) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('order.packing_list.notes_label')}
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('order.packing_list.notes_placeholder')}
          />
        </div>

        {/* Items table */}
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">
                    {t('order.packing_list.product_column')}
                  </th>
                  <th className="text-left p-3 font-medium text-gray-600 w-48">
                    {t('order.packing_list.qty_column')}
                  </th>
                  <th className="p-3 w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, idx) => (
                  <tr key={`${row.platformProductLengthId}-${idx}`}>
                    <td className="p-3 text-gray-900">{row.productLabel}</td>
                    <td className="p-3">
                      <BundleQuantityInput
                        quantity={{
                          value: row.quantityDisplay,
                          unit: row.unitLabel,
                        }}
                        platformProductsLength={row.platformProductsLength}
                        piecesPerBundle={
                          row.piecesPerBundleOverride ?? undefined
                        }
                        orderLinePiecesPerBundle={orderLinePiecesPerBundleFor(
                          row.platformProductLengthId
                        )}
                        onPiecesPerBundleChange={(value, quantity) =>
                          setPiecesPerBundleOverride(idx, value, quantity)
                        }
                        onPiecesPerBundleClear={(quantity) =>
                          setPiecesPerBundleOverride(idx, null, quantity)
                        }
                        onBundleChange={(q) =>
                          setRows((prev) =>
                            prev.map((r, i) =>
                              i === idx
                                ? {
                                    ...r,
                                    quantityDisplay: q.value,
                                    unitLabel: q.unit,
                                  }
                                : r
                            )
                          )
                        }
                      />
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() =>
                          setRows((prev) => prev.filter((_, i) => i !== idx))
                        }
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        {t('order.packing_list.remove_button')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {useSellerAddFlow ? (
            <div className="mt-4 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 shrink-0">
                {t('order.packing_list.add_row_label')}
              </label>
              <select
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddRow(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">
                  {t('order.packing_list.select_product_placeholder')}
                </option>
                {products
                  .filter(
                    (p) => p && !usedPplIds.has(p.platformProductLength.id)
                  )
                  .map((p) => (
                    <option
                      key={p.platformProductLength.id}
                      value={p.platformProductLength.id}
                    >
                      {p.platformProductLength.platformProduct.name} &ndash;{' '}
                      {formatLengthWithBasis(
                        p.platformProductLength.lengthDisplayValue,
                        p.platformProductLength.isNominal
                      )}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div className="mt-4">
              <Button onClick={() => setShowAddItemModal(true)}>
                {t('order.packing_list.add_item_button')}
              </Button>
            </div>
          )}
        </div>

        {/* Zero-row guard */}
        {rows.length === 0 && (
          <p className="text-sm text-red-600">
            {t('order.packing_list.min_items_error')}
          </p>
        )}

        {/* Footer buttons */}
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={rows.length === 0 || isSaving}>
            {t('order.packing_list.save_button')}
          </Button>
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('order.packing_list.cancel_button')}
          </button>
        </div>
      </div>

      <Modal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        className="w-full max-w-lg"
      >
        <AddPackingListItemModal
          currency={currency}
          usedPplIds={usedPplIds}
          onClose={() => setShowAddItemModal(false)}
          onAdd={handleAddBuyerItem}
        />
      </Modal>
    </div>
  );
}
